import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, resetRegisterFlag } from "slices/thunk";
import { createSelector } from 'reselect';
import { Facebook, Github, Mail, Twitter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

// Image
import { RootState } from "slices";
import ModernImage from "pages/AuthenticationInner/ModernImage";
import Modern from "pages/AuthenticationInner/Modern";

const Register = () => {

  document.title = "Register | EngageHub IQ";

  const dispatch = useDispatch<any>();
  const navigation = useNavigate();

  const selectRegister = createSelector(
    (state: RootState) => state.Register,
    (register) => ({
      success: register.success,
      error: register.error
    })
  )

  const { success, error } = useSelector(selectRegister)

  const validation: any = useFormic({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email().required("Please Enter Your Email"),
      username: Yup.string().required("Please Enter Your Username"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values: any) => {
      dispatch(registerUser(values));
    }
  });

  React.useEffect(() => {

    if (success) {
      setTimeout(() => {
        navigation('/login')
      }, 1000)
    }

    setTimeout(() => {
      dispatch(resetRegisterFlag());
    }, 3000);

  }, [dispatch, success, navigation]);

  return (
    <React.Fragment>
      <div className="grid grid-cols-3  to-custom-800 bg-gradient-to-r from-custom-900 m-1 min-h-screen rounded-md overflow-auto">
        <ModernImage />
        <div className="m-4 col-span-1 card shadow-lg z-10">
          <div className="!px-10 !py-12 card-body">
            <div className="mt-8 text-center">
              <h4 className="mb-1 text-custom-500 dark:text-custom-500">Create your account</h4>
            </div>

            <form action="/" className="mt-10" id="registerForm"
              onSubmit={(event: any) => {
                event.preventDefault();
                validation.handleSubmit();
                return false;
              }}>
              {success && <div className="px-4 py-3 mb-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50" id="successAlert">
                You have signed up <b>successfully</b>.
              </div>}
              {error !== '' && <div className="px-4 py-3 mb-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50" id="errorAlert">
                {error?.includes('Passwords') ? (
                  <span>Password must be at least 6 characters and include uppercase, lowercase, and a special character.</span>
                ) : error?.includes('Username') ? (
                  <span>{error}</span>
                ) : error?.includes('Email ') ? (
                  <span>{error}</span>
                ) : (<></>)}
              </div>}
              <div className="mb-3">
                <label htmlFor="email-field" className="inline-block mb-2 text-base font-medium">Email</label>
                <input
                  type="text"
                  id="email-field"
                  name="email"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""} />
                {validation.touched.email && validation.errors.email ? (
                  <div id="email-error" className="mt-1 text-sm text-red-500">{validation.errors.email}</div>
                ) : null}
              </div>
              <div className="mb-3">
                <label htmlFor="username-field" className="inline-block mb-2 text-base font-medium">UserName</label>
                <input
                  type="text"
                  id="username-field"
                  name="username"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter username"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.username || ""} />
                {validation.touched.username && validation.errors.username ? (
                  <div id="username-error" className="mt-1 text-sm text-red-500">{validation.errors.username}</div>
                ) : null}
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="inline-block mb-2 text-base font-medium">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password || ""} />
                {validation.touched.password && validation.errors.password ? (
                  <div id="password-error" className="mt-1 text-sm text-red-500">{validation.errors.password}</div>
                ) : null}
              </div>
              <p className="italic text-15 text-slate-500 dark:text-zink-200">By registering you agree to the app <a href="#!" className="underline">Terms of Use</a></p>
              <div className="mt-10">
                <button type="submit" className="w-full text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">Sign Up</button>
              </div>

              <div className="relative text-center my-9 before:absolute before:top-3 before:left-0 before:right-0 before:border-t before:border-t-slate-200 dark:before:border-t-zink-500">
                <h5 className="inline-block px-2 py-0.5 text-sm bg-white text-slate-500 dark:bg-zink-600 dark:text-zink-200 rounded relative">Create account with</h5>
              </div>

              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" className="flex items-center justify-center size-[37.5px] transition-all duration-200 ease-linear p-0 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 active:text-white active:bg-custom-600 active:border-custom-600">
                  <Facebook className="size-4"></Facebook>
                </button>
                <button type="button" className="flex items-center justify-center size-[37.5px] transition-all duration-200 ease-linear p-0 text-white btn bg-orange-500 border-orange-500 hover:text-white hover:bg-orange-600 hover:border-orange-600 focus:text-white focus:bg-orange-600 focus:border-orange-600 active:text-white active:bg-orange-600 active:border-orange-600">
                  <Mail className="size-4"></Mail>
                </button>
                <button type="button" className="flex items-center justify-center size-[37.5px] transition-all duration-200 ease-linear p-0 text-white btn bg-sky-500 border-sky-500 hover:text-white hover:bg-sky-600 hover:border-sky-600 focus:text-white focus:bg-sky-600 focus:border-sky-600 active:text-white active:bg-sky-600 active:border-sky-600">
                  <Twitter className="size-4"></Twitter>
                </button>
                <button type="button" className="flex items-center justify-center size-[37.5px] transition-all duration-200 ease-linear p-0 text-white btn bg-slate-500 border-slate-500 hover:text-white hover:bg-slate-600 hover:border-slate-600 focus:text-white focus:bg-slate-600 focus:border-slate-600 active:text-white active:bg-slate-600 active:border-slate-600">
                  <Github className="size-4"></Github>
                </button>
              </div>

              <div className="mt-10 text-center">
                <p className="mb-0 text-slate-500 dark:text-zink-200">Already have an account ? <Link to="/login" className="font-semibold underline transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500">Login</Link> </p>
              </div>
            </form>
          </div>
        </div>
        <div className="col-span-2">
          <Modern />
        </div>
      </div>
    </React.Fragment>
  );
}

export default Register;